<template>
  <NavBar :theme="theme" :key="theme" />

  <div class="mx-auto max-w-3xl px-4 py-24 sm:px-6 lg:max-w-7xl lg:px-8">
    <div class="grid grid-cols-1 items-start gap-4 lg:gap-8 xl:grid-cols-2">
      <div class="grid grid-cols-1 gap-4">
        <div class="overflow-hidden rounded-lg bg-background">
          <div class="p-6 pt-6">
            <p class="pb-2 text-2xl font-bold">
              {{ t("msg.hello", { name: user.Name }) }}
            </p>
            <p>
              {{ t("msg.sessionActive") }}
            </p>
            <p class="text-7xl">{{ globals.formatDuration(duration) }}</p>
            <p class="pt-8">{{ t("msg.yourCurrentIp", { address: user.CurrentAddress }) }}</p>
            <p class="pt-8 italic" style="font-weight: bold; width: 70%">
              <span>{{ t("msg.keepThisPageOpen") }}</span>
            </p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 overflow-hidden rounded-lg">
        <table class="mt-6 w-full whitespace-nowrap p-6 text-left">
          <thead class="border-b text-sm leading-6">
            <tr>
              <th scope="col" class="font-semibold">{{ t("msg.description") }}</th>
              <th scope="col" class="flex justify-end font-semibold sm:justify-start">
                {{ t("msg.status") }}
              </th>
              <th scope="col" class="flex justify-end font-semibold sm:justify-start">
                <span class="sr-only">{{ t("common.actions") }}</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-white/5">
            <tr v-for="acl in deDuplicatedAcls" :key="acl.ID">
              <td class="text-sm font-medium leading-6">
                <template v-if="acl.URL">
                  <a class="flex flex-1 flex-row" :href="acl.URL" target="_blank">
                    {{ acl.Description }} &nbsp;
                    <ArrowTopRightOnSquareIcon class="h-5 w-5" />
                  </a>
                </template>
                <span v-else>{{ acl.Description }}</span>
              </td>
              <td class="py-4 text-sm leading-6">
                <div class="flex items-center justify-end gap-x-2 sm:justify-start">
                  <div :class="[globals.statuses[acl.Status].icon, 'flex-none rounded-full p-1']">
                    <div class="h-1.5 w-1.5 rounded-full bg-current" />
                  </div>
                  <span v-if="acl.Error" :title="acl.Error">
                    {{ t(globals.statuses[acl.Status].name) }}
                  </span>
                  <span v-else>{{ t(globals.statuses[acl.Status].name) }}</span>
                </div>
              </td>
              <td>
                <template v-if="acl.ClickToGrant && !getAclStatus(acl.Status).error">
                  <button
                    v-if="
                      getAclStatus(acl.Status)?.grantIssued ||
                      (Boolean(expiredAfter(acl.ExpiredAfter)) && expiresDiff(expiredAfter(acl.ExpiredAfter)) > 0)
                    "
                    class="themed-button"
                    :class="(Boolean(theme) && `theme-${theme}`) || 'theme-default'"
                    type="button"
                    @click="revokeAcl(acl)"
                    data-swapy-no-drag
                  >
                    Revoke Access
                  </button>
                  <button
                    v-else-if="getAclStatus(acl.Status).grantReady"
                    class="themed-button"
                    :class="(Boolean(theme) && `theme-${theme}`) || 'theme-default'"
                    @click="grantAcl(acl)"
                    type="button"
                    data-swapy-no-drag
                  >
                    Grant Access
                  </button>
                  <button
                    v-else-if="getAclStatus(acl.Status).pending"
                    class="themed-button theme-default cursor-pointer"
                    @click="undefined"
                    type="button"
                    data-swapy-no-drag
                  >
                    Pending
                  </button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <Dialog :show="isLicensed === false">
    <DialogOverlay />

    <DialogPanel :theme="theme" class="!max-w-[512px]">
      <DialogTitle>
        {{ t("pages.home.dialogs.unlicensed.heading") }}
      </DialogTitle>

      <DialogDescription>
        {{ t("pages.home.dialogs.unlicensed.description") }}
      </DialogDescription>

      <div class="flex space-x-3">
        <button
          :class="
            twMerge(
              'inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:col-start-2',
              theme && theme === 'teal' && 'bg-[#36b59c] focus-visible:outline-[#36b59c]',
              theme && theme === 'grape' && 'bg-[#8259fa] focus-visible:outline-[#8259fa]',
              theme && theme === 'iceberg' && 'bg-[#247aa1] focus-visible:outline-[#247aa1]',
              theme && theme === 'rose' && 'bg-primary focus-visible:outline-primary',
              theme && theme === 'offsite' && 'bg-[#ccd1c4] focus-visible:outline-[#ccd1c4]',
            )
          "
          @click="handleLogout"
        >
          {{ t("common.logout") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <ErrorNotification :error="errorMessage" title="Warning" :close="false" />
</template>

<script setup>
import { DialogDescription } from "@headlessui/vue";
import { twMerge } from "tailwind-merge";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import globals from "../globals";
import authService from "../services/auth.service.ts";

import NavBar from "../components/NavBar.vue";
import ErrorNotification from "../components/ErrorNotification.vue";
import { ArrowTopRightOnSquareIcon } from "@heroicons/vue/24/outline";

import { Dialog, DialogOverlay, DialogPanel, DialogTitle } from "../components/ui/dialog";
import { DateTime } from "luxon";
import { useAuthStore } from "../pinia/auth.ts";
import { getAclStatus } from "../shared/services/aclStatus.js";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const authStore = useAuthStore();
authStore.fetchTheme();

const user = computed(() => authStore.user);
const theme = computed(() => authStore.theme.Accent);
const highAccessibility = computed(() => authStore.theme.HighAccessibility);
const isLicensed = computed(() => authStore.user?.Licensed);

const duration = ref(globals.timeRemaining(user.value?.Ends));
const errorMessage = ref("");

var statusInterval, countdown;

function expiredAfter(timestamp) {
  if (!timestamp) {
    return null;
  }

  return DateTime.fromISO(timestamp, { zone: "utc" });
}

function expiresDiff(after) {
  return after ? after.diffNow("seconds").seconds : null;
}

let ports = [443, 222, 3389, 8000];
const ipApi = ref("");
const walkedIPs = ref([]);

const deDuplicatedAcls = computed(() => {
  if (!user.value.ACLs) return [];

  let uniqueAcls = [];
  user.value.ACLs.filter((acl) => !acl.HideFromUsers).forEach((acl) => {
    if (!uniqueAcls.find((a) => a.Description === acl.Description)) {
      uniqueAcls.push(acl);
    }
  });

  return uniqueAcls;
});

async function fetchStatus() {
  await authStore.fetchStatus({ force: true });

  authStore.user.ACLs.forEach((acl) => {
    if (!acl.ClickToGrant && acl.ID == "00000000-0000-0000-0000-000000000000") {
      if (acl.CGNATOpen && ipApi.value.length > 0) {
        handleGrantAcl(acl);
      }
    }
  });
}

let grantRevokeIntervals = {};

function createGrantRevokeCallback(aclId) {
  if (grantRevokeIntervals[aclId]) {
    clearInterval(grantRevokeIntervals[aclId]);
    delete grantRevokeIntervals[aclId];
  }

  grantRevokeIntervals[aclId] = setInterval(checkIfGrantRevokeFinished, 1000, aclId, 0, 1000);
}

async function checkIfGrantRevokeFinished(aclId, previousTimeout, timeout) {
  await fetchStatus();

  if (grantRevokeIntervals[aclId]) {
    clearInterval(grantRevokeIntervals[aclId]);
    delete grantRevokeIntervals[aclId];
  }

  const acl = authStore.user.ACLs.find((acl) => acl.ACLID === aclId);
  if (!acl) {
    console.warn("Could not find ACL set for interval with ID", aclId);
    return;
  }

  if (acl.Status === 1 || acl.Status === 3 || acl.Status === 4 || acl.Status === 6) {
    return;
  }

  const newTimeout = timeout + previousTimeout;
  grantRevokeIntervals[aclId] = setInterval(checkIfGrantRevokeFinished, newTimeout, acl.ACLID, timeout, newTimeout);
}

async function grantAcl(acl) {
  let ips = [{ Port: 0, IP: user.value.CurrentAddress }];

  if (acl.CGNATOpen && ipApi.value.length > 0) {
    ips = walkedIPs.value;
  }

  await authService.grantAcl(acl.ACLID, ips, acl.TicketNumber, acl.TOTPCode);
  await fetchStatus();

  createGrantRevokeCallback(acl.ACLID);
}

async function revokeAcl(acl) {
  await authService.revokeAcl(acl.ACLID);

  await fetchStatus();

  createGrantRevokeCallback(acl.ACLID);
}

async function handleLogout() {
  try {
    await authStore.logout();
  } finally {
    router.push({ name: "Login", query: route.query });
  }
}

onUnmounted(() => {
  clearInterval(countdown);
  clearInterval(statusInterval);

  for (interval of Object.values(grantRevokeIntervals)) {
    clearInterval(interval);
  }
});

watch(
  () => user.value?.CurrentAddress,
  async (newIP, oldIP) => {
    if (newIP !== oldIP) {
      let statusResponse = await authService.getSystemStatus();
      ipApi.value = statusResponse.data.IPApi;
      if (ipApi.value.length > 0) {
        try {
          const ipPromises = ports.map(async (port) => {
            const response = await authService.getIp(ipApi.value, port);
            return { Port: port, IP: response.data };
          });

          walkedIPs.value = await Promise.all(ipPromises);

          await authService.logIps(walkedIPs.value);
        } catch (error) {
          // console.log(error)
        }
      }
    }
  },
  { immediate: true, deep: true },
);

onMounted(() => {
  countdown = setInterval(() => {
    duration.value = globals.timeRemaining(user.value.Ends);
    if (duration.value <= 0) {
      router.push({ name: "Login", query: route.query });
      clearInterval(countdown);
      clearInterval(statusInterval);
    }
  }, 1000);

  fetchStatus();
  statusInterval = setInterval(fetchStatus, 30000);
});
</script>

<style scoped>
.themed-button {
  @apply inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border bg-background px-3 text-sm font-medium text-primary-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50;
}

.theme-teal {
  @apply bg-[#36b59c] hover:bg-[#36b59c]/90 focus-visible:outline-[#36b59c];
}

.theme-grape {
  @apply bg-[#8259fa] hover:bg-[#8259fa]/90 focus-visible:outline-[#8259fa];
}

.theme-iceberg {
  @apply bg-[#247aa1] hover:bg-[#247aa1]/90 focus-visible:outline-[#247aa1];
}

.theme-rose {
  @apply bg-primary hover:bg-primary/90 focus-visible:outline-primary;
}

.theme-offsite {
  @apply bg-[#ccd1c4] hover:bg-[#ccd1c4]/90 focus-visible:outline-[#ccd1c4];
}

.theme-default {
  @apply bg-[#8259fa] hover:bg-[#8259fa]/90 focus-visible:outline-[#8259fa];
}
</style>
